import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";
import { AnimatePresence, motion, useScroll, useTransform } from "framer-motion";
import { useLang } from "@/lib/i18n";
import { useSite } from "@/lib/site";
import { MaskLines } from "@/components/Reveal";
import YouTubeBackground, { youtubeId } from "@/components/YouTubeBackground";
import Img from "@/components/Img";
import { useIsMobile, SCRIM_GRADIENT } from "@/lib/heroMedia";

function SlideMedia({ slide, onEnded, isMobile }) {
  const focal = (isMobile ? slide.focal_mobile : slide.focal) || "center";
  const fit = { objectPosition: focal };
  const [videoFailed, setVideoFailed] = useState(false);

  if (slide.kind === "youtube") {
    const id = youtubeId(slide.youtube_url);
    if (id)
      return (
        <YouTubeBackground
          videoId={id}
          start={slide.yt_start || 0}
          end={slide.yt_end || 0}
          poster={slide.poster_url || slide.image_url}
          posterStyle={fit}
        />
      );
  }
  if (slide.kind === "video" && slide.video_url && !videoFailed) {
    return (
      <video
        data-testid="hero-video"
        src={slide.video_url}
        poster={slide.poster_url}
        autoPlay
        muted
        loop={false}
        playsInline
        onEnded={onEnded}
        onError={() => setVideoFailed(true)}
        style={fit}
        className="h-full w-full object-cover"
      />
    );
  }
  const src = (isMobile && slide.image_url_mobile) || slide.image_url || slide.poster_url;
  return (
    <Img
      data-testid="hero-image"
      src={src}
      width={isMobile ? 960 : 2560}
      sizes="100vw"
      eager
      alt=""
      style={fit}
      className="h-full w-full object-cover"
    />
  );
}

export default function HeroStage() {
  const { t, L, dir } = useLang();
  const site = useSite();
  const isMobile = useIsMobile();
  const s = site?.settings || {};

  const slides = useMemo(() => {
    const hideStancor = s.spotlight?.enabled === false || s.spotlight?.show_hero === false;
    const configured = (s.hero_slides || [])
      .filter((sl) => sl.image_url || sl.video_url || sl.youtube_url)
      .filter((sl) => !(hideStancor && sl.id === "slide-stancor"));
    if (configured.length) return configured;
    return [{ id: "legacy", kind: "video", video_url: s.video_url, poster_url: s.poster_url, duration: 12 }];
  }, [s.hero_slides, s.video_url, s.poster_url, s.spotlight]);

  const [index, setIndex] = useState(0);
  const slide = slides[Math.min(index, slides.length - 1)];

  const go = useCallback((delta) => setIndex((i) => (i + delta + slides.length) % slides.length), [slides.length]);
  const next = useCallback(() => go(1), [go]);

  const timerRef = useRef(null);
  useEffect(() => {
    if (slides.length < 2) return undefined;
    const trimmed = slide.kind === "youtube" && slide.yt_end > slide.yt_start ? slide.yt_end - slide.yt_start : 0;
    const seconds = trimmed || slide.duration || 8;
    timerRef.current = setTimeout(next, seconds * 1000);
    return () => clearTimeout(timerRef.current);
  }, [index, slide, slides.length, next]);

  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const mediaY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const mediaScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.2]);
  const contentY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const contentFade = useTransform(scrollYProgress, [0, 0.7], [1, 0.2]);

  const eyebrow = L(slide.eyebrow) || L(s.hero_eyebrow);
  const slideLines = (slide.lines || []).map((l) => L(l)).filter(Boolean);
  const globalLines = (s.hero_lines || []).map((l) => L(l)).filter(Boolean);
  const lines = slideLines.length ? slideLines : globalLines.length ? globalLines : ["Al Qalaa", "International Group"];
  const tagline = L(slide.tagline) || L(s.hero_tagline);
  const highlights = (slide.highlights || []).length ? slide.highlights : s.stats || [];

  return (
    <section ref={heroRef} data-testid="video-hero" className="relative min-h-screen flex items-end overflow-hidden">
      <AnimatePresence mode="sync">
        <motion.div
          key={slide.id || index}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1.1, ease: "easeInOut" }}
          className="absolute inset-0"
        >
          <motion.div style={{ y: mediaY, scale: mediaScale }} className="absolute inset-0 will-change-transform">
            <SlideMedia slide={slide} isMobile={isMobile} onEnded={slides.length > 1 ? next : undefined} />
          </motion.div>
        </motion.div>
      </AnimatePresence>

      <div className="hero-grid-overlay industrial-grid absolute inset-0" aria-hidden="true" />
      <div
        className="hero-glow absolute inset-0"
        style={{ background: "radial-gradient(ellipse at center, hsl(var(--primary)/0.16), transparent 62%)" }}
        aria-hidden="true"
      />
      <div className="absolute inset-0" style={{ background: "var(--hero-overlay)" }} />
      {SCRIM_GRADIENT[slide.scrim] ? (
        <div className="absolute inset-0" data-testid="hero-scrim" style={{ background: SCRIM_GRADIENT[slide.scrim] }} aria-hidden="true" />
      ) : null}

      <motion.div
        style={{ y: contentY, opacity: contentFade }}
        className="hero-inner relative mx-auto w-full max-w-7xl px-5 sm:px-8 pt-36 pb-10 flex flex-col"
      >
        <motion.p
          key={`eyebrow-${index}`}
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="eyebrow mb-6"
          data-testid="hero-eyebrow"
        >
          {eyebrow}
        </motion.p>

        <MaskLines
          key={`lines-${index}`}
          load
          lines={lines}
          className={`font-head font-medium text-[clamp(2.9rem,9vw,7.6rem)] leading-[1.02] max-w-5xl ${dir === "rtl" ? "text-right" : ""}`}
          lineClassName="text-glow"
        />

        <motion.p
          key={`tagline-${index}`}
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.5 }}
          className="mt-7 max-w-2xl text-muted-foreground text-base sm:text-lg leading-relaxed"
          data-testid="hero-tagline"
        >
          {tagline}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.65 }}
          className="hero-cta-row mt-9 flex flex-wrap gap-4"
        >
          <Link to="/sectors" data-testid="hero-cta-primary" className="btn-gold">
            {t("hero_cta_primary")} <ArrowRight className="h-4 w-4 rtl:rotate-180" />
          </Link>
          <Link to="/joint-ventures" data-testid="hero-cta-secondary" className="btn-outline">
            {t("hero_cta_secondary")}
          </Link>
        </motion.div>

          {slides.length > 1 && (
          <div className="hero-cta-row mt-12 flex w-full items-center gap-4" data-testid="hero-controls">
            <button
              type="button"
              data-testid="hero-prev"
              aria-label={t("hero_prev")}
              onClick={() => go(-1)}
              className="flex h-9 w-9 items-center justify-center rounded-full border border-foreground/25 text-foreground/70 transition-colors hover:border-primary hover:text-primary"
            >
              <ChevronLeft className="h-4 w-4 rtl:rotate-180" />
            </button>
            <div className="flex items-center">
              {slides.map((sl, i) => (
                <button
                  key={sl.id || i}
                  type="button"
                  data-testid={`hero-dot-${i}`}
                  aria-label={`${t("hero_slide")} ${i + 1}`}
                  onClick={() => setIndex(i)}
                  className="group flex h-9 w-9 items-center justify-center"
                >
                  <span
                    className={`block h-1.5 rounded-full transition-all duration-500 ${
                      i === index ? "w-8 bg-primary" : "w-1.5 bg-foreground/35 group-hover:bg-foreground/60"
                    }`}
                  />
                </button>
              ))}
            </div>
            <button
              type="button"
              data-testid="hero-next"
              aria-label={t("hero_next")}
              onClick={next}
              className="flex h-9 w-9 items-center justify-center rounded-full border border-foreground/25 text-foreground/70 transition-colors hover:border-primary hover:text-primary"
            >
              <ChevronRight className="h-4 w-4 rtl:rotate-180" />
            </button>
          </div>
          )}

        <div
          key={`stats-${index}`}
          data-testid="hero-stats"
          className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-6 border-t border-foreground/10 pt-8"
        >
          {highlights.map((st, i) => (
            <motion.div key={`${index}-${i}`} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.8 + i * 0.1 }}>
              <p className="font-head text-3xl md:text-4xl text-primary" data-testid={`hero-stat-value-${i}`}>{st.value}</p>
              <p className="mt-1 text-xs tracking-[0.2em] uppercase text-muted-foreground">{L(st.label)}</p>
            </motion.div>
          ))}
        </div>
      </motion.div>

      <div className="absolute bottom-6 end-6 hidden md:flex flex-col items-center gap-2 text-muted-foreground">
        <span className="text-[10px] tracking-[0.3em] uppercase">{t("scroll")}</span>
        <span className="scroll-hint inline-block h-8 w-px bg-primary/70" aria-hidden="true" />
      </div>
    </section>
  );
}
